import * as Dialog from "@radix-ui/react-dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Content, BackDrop } from "@volumental/common";
import type React from "react";
import { cn } from "../../lib/cn";
import { Header } from "../accessible-components";

export interface ModalProps {
  show: boolean;
  fitContent?: boolean;
  close?: () => void;
  back?: () => void;
  headerTitle?: string;
  disableGutters?: boolean;
  disableScrollBar?: boolean;
  modalTitle: string;
}

export function Modal({
  show,
  fitContent = false,
  close,
  back,
  headerTitle,
  modalTitle,
  disableGutters = false,
  disableScrollBar = true,
  children,
}: React.PropsWithChildren<ModalProps>): React.JSX.Element | null {
  const shouldHaveHeader = back !== undefined || close !== undefined || headerTitle !== undefined;
  return (
    <Dialog.Root open={show} onOpenChange={(open) => !open && close?.()}>
      <Dialog.Portal>
        <div className="fixed inset-0 z-2">
          <BackDrop visible={show} animationTime="0.3s" onClick={close} />
          <Dialog.Content
            aria-describedby={undefined}
            className={cn(
              "fixed bottom-0 left-0 w-full bg-white rounded-tl-3xl rounded-tr-3xl flex flex-col animate-sidebar-slide-in z-3 overflow-hidden outline-none",
              fitContent ? "max-h-[calc(100%-34px)]" : "h-[calc(100%-34px)]",
              !shouldHaveHeader && "pt-6"
            )}
            onEscapeKeyDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              close?.();
            }}
          >
            <Dialog.Title asChild>
              <VisuallyHidden>{modalTitle}</VisuallyHidden>
            </Dialog.Title>
            {shouldHaveHeader && <Header close={close} back={back} title={headerTitle} />}
            <Content fitToContent disableGutters={disableGutters} disableScrollbar={disableScrollBar}>
              {children}
            </Content>
          </Dialog.Content>
        </div>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
